import { isEmpty, get } from 'lodash';
import { policySearchRequest, sectionApi, titleOpinionApi } from '~/api';
import { sectionTypes } from './sections';

export default {
    prefillFromPTO,
    prefillFromSearch
};

/**
 * Creates an FTO record using data from a policySearchRequest
 * 
 * @param {(string|number)} searchId The id of the policySearchRequest
 * @param {string} fileNumber The fileNumber to use for the titleOpinion record
 * @returns {Promise<number>} A Promise that fulfills with the id of the new titleOpinion record
 */
export async function prefillFromSearch(searchId, fileNumber, commitmentNumber) {
    const data = await policySearchRequest.getById(searchId);


    const address = parseSearchFormAddress(data);
    const owner = parseSearchFormOwner(data);

    const fileSection = await createFileSection(fileNumber, 0);
    await createDeed(fileSection.sectionId, owner, '');
    await createAdditionalInfo(fileSection.titleOpinionId, address, '');

    if (commitmentNumber) {
        await sectionApi.updateSection({
            id: fileSection.sectionId,
            path: 'commitmentNumber',
            value: commitmentNumber
        });
    }

    return fileSection.titleOpinionId;
}

export function parseSearchFormAddress(data) {
    const {
        streetNumber,
        streetName,
        streetSuffix,
        city,
        state,
        zipcode
    } = data._data;

    const street = `${streetNumber} ${streetName} ${streetSuffix}`;
    return `${street}\n${city}, ${state} ${zipcode}`;
}

export function parseSearchFormOwner(data) {
    const {
        ownerFirstName,
        ownerMiddleName,
        ownerLastName,
        ownerSuffix
    } = data._data;

    return ownerMiddleName
        ? `${ownerFirstName} ${ownerMiddleName} ${ownerLastName} ${ownerSuffix}`
        : `${ownerFirstName} ${ownerLastName} ${ownerSuffix}`;
}

/**
 * Prefills FTO sections using data from an existing PTO record
 * @param {(string|number)} titleOpinionId 
 * @returns {Promise<void>}
 */
export async function prefillFromPTO(titleOpinionId) {
    const details = await titleOpinionApi.getTitleOpinionDetails(titleOpinionId);

    const fileNumber = details.titleOpinion.fileNumber;
    const commitmentNumber = details.titleOpinion.orderNumber;

    const owners = parseOwners(details);
    let insured = parseInsured(details);

    if (/$^/.test(insured) || /^owner$/i.test(insured))
        insured = owners;

    const lender = parseLender(details);

    const address = getPropertyAddress(details);

    const fileSection = await createFileSection(fileNumber, titleOpinionId);
    await createDeed(fileSection.sectionId, owners, insured);
    await createDeedOfTrust(titleOpinionId, insured, lender);
    await createAdditionalInfo(titleOpinionId, address, lender);

    if (commitmentNumber) {
        await sectionApi.updateSection({
            id: fileSection.sectionId,
            path: 'commitmentNumber',
            value: commitmentNumber
        });
    }
}

async function createFileSection(fileNumber, titleOpinionId) {
    const args = {
        value: fileNumber,
        path: 'fileNumber',
        sectionTypeId: sectionTypes.FILE,
        titleOpinionId
    };

    return sectionApi.createSection(args);
}

export function parseOwners(data) {
    const { titleOpinionDetails } = data;

    if (!Array.isArray(titleOpinionDetails))
        return null;

    const propertyInfo = titleOpinionDetails.filter(d => d.sectionType == 1)[0];

    if (!propertyInfo || propertyInfo.sectionDetails == undefined)
        return null;

    const rawOwners = Array.isArray(propertyInfo.sectionDetails)
        ? propertyInfo.sectionDetails.map(d => ({ ...d.details }))
        : [];

    if (rawOwners.length < 1)
        return null;

    return rawOwners.reduce((result, ownerData) => {
        let owner = '';

        const primaryType = get(ownerData, 'ownerPrimaryType.value', '');

        if (/individual/i.test(primaryType)) {
            owner += parseIndividualName(ownerData.ownerPrimary);
        } else if (/entity/i.test(primaryType)) {
            owner += parseEntityName(ownerData.ownerPrimary);
        }

        if (typeof ownerData.ownerSecondary == 'object' && !isEmpty(ownerData.ownerSecondary)) {
            const secondaryType = get(ownerData, 'ownerSecondaryType.value', '');

            if (/individual/i.test(secondaryType)) {
                owner += ` and ${parseIndividualName(ownerData.ownerSecondary)}`;
            } else if (/entity/i.test(secondaryType)) {
                owner += ` and ${parseEntityName(ownerData.ownerSecondary)}`;
            }
        }

        return result.length > 0
            ? `${result}, \n${owner}`
            : owner;
    }, '');
}

export function parseIndividualName(person, propKey = 'owner') {
    if (typeof person != 'object' || isEmpty(person))
        return '';

    return [
        person[`${propKey}FirstName`],
        person[`${propKey}MiddleName`],
        person[`${propKey}LastName`],
        person[`${propKey}Suffix`]
    ]
        .filter(s => !!s)
        .join(' ');
}

export function parseEntityName(entity, propKey = 'owner') {
    if (typeof entity != 'object' || isEmpty(entity))
        return '';

    return `${entity[`${propKey}EntityName`]}, ${entity[`${propKey}EntityType`].label}`;
}

export function parseInsured(data) {
    const { titleOpinionDetails } = data;

    if (!Array.isArray(titleOpinionDetails))
        return null;

    const titleInsurance = titleOpinionDetails.filter(d => d.sectionType == 7)[0];

    if (!titleInsurance || isEmpty(titleInsurance.sectionData))
        return null;

    if (get(titleInsurance, 'sectionData.sameAsOwner', false))
        return 'owner';

    if (get(titleInsurance, 'sectionData.needOwnerCoverage', '') == 'No')
        return '';

    const rawInsured = Array.isArray(titleInsurance.sectionDetails)
        ? titleInsurance.sectionDetails.map(d => ({ ...d.details }))
        : [];

    if (rawInsured.length < 1)
        return '';

    return rawInsured
        .reduce((result, insuredData) => {

            const t = get(insuredData, 'type', '');

            if (!/insured/i.test(t))
                return result;

            let insured = '';

            const primaryType = get(insuredData, 'insuredPrimaryType.value', '');

            if (/individual/i.test(primaryType)) {
                insured += parseIndividualName(insuredData.insuredPrimary, 'insured');
            } else if (/entity/i.test(primaryType)) {
                insured += parseEntityName(insuredData.insuredPrimary, 'insured');
            }

            if (typeof insuredData.insuredSecondary == 'object' && !isEmpty(insuredData.insuredSecondary, 'insured')) {
                const secondaryType = get(insuredData, 'insuredSecondaryType.value', '');

                if (/individual/i.test(secondaryType)) {
                    insured += ` and ${parseIndividualName(insuredData.insuredSecondary, 'insured')}`;
                } else if (/entity/i.test(secondaryType)) {
                    insured += ` and ${parseEntityName(insuredData.insuredSecondary, 'insured')}`;
                }
            }

            return result.length > 0
                ? `${result}, \n${insured}`
                : insured;
        }, '');
}

export function parseLender(data) {
    const titleInsurance = getSection(data, 7);

    if (titleInsurance == null) return null;

    const details = get(titleInsurance, 'sectionDetails', []);

    if (!Array.isArray(details) || details.length < 1)
        return null;

    const lenders = details.map(l => ({ ...l.details }))
        .filter(d => d != null && !isEmpty(d));

    return lenders.reduce((result, lender) => {
        const lenderData = get(lender, 'lenderDetailData', {});

        if (isEmpty(lenderData))
            return result;

        const {
            lenderName,
            lenderStreetAddress,
            lenderCity,
            lenderState,
            lenderZip
        } = lenderData;

        const formattedLender = `${lenderName}\n${lenderStreetAddress}\n${lenderCity}, ${lenderState} ${lenderZip}`;

        return result.length > 0
            ? `${result}\n\n${formattedLender}`
            : `${formattedLender}`;
    }, '');
}

export function getPropertyAddress(data) {

    let maybeAddress = get(data, 'titleOpinion.propertyAddress', null);

    if (maybeAddress != null)
        return maybeAddress;

    const propertyInfo = getSection(data, 1);

    if (propertyInfo == null) return null;

    const { sectionData } = propertyInfo;

    const {
        street,
        city,
        state = {},
        zipcode
    } = sectionData;

    const stateCode = get(state, 'value', '');

    return `${street}\n${city}, ${stateCode} ${zipcode}`;
}

function getSection(data, sectionType) {
    const { titleOpinionDetails } = data;

    if (!Array.isArray(titleOpinionDetails))
        return null;

    const section = titleOpinionDetails.filter(d => d.sectionType == sectionType)[0];

    if (section == null || isEmpty(section))
        return null;

    return section;
}

async function createDeed(sectionId, owners, insured) {
    const payload = {
        sectionId,
        grantors: owners,
        grantees: insured
    };

    return sectionApi.saveSectionDetail(payload);
}

async function createDeedOfTrust(titleOpinionId, insured, lender) {
    const sectionPayload = {
        titleOpinionId,
        sectionTypeId: sectionTypes.DEED
    };

    return sectionApi.createSection(sectionPayload)
        .then(async newSection => {
            const deedPayload = {
                sectionId: newSection.sectionId,
                mortgators: insured,
                beneficiary: lender
            };

            return sectionApi.saveSectionDetail(deedPayload);
        });
}

async function createAdditionalInfo(titleOpinionId, propertyAddress, lender) {
    const payload = {
        titleOpinionId,
        sectionTypeId: sectionTypes.ADDITIONAL,
        path: 'ownerPolicyRecipient',
        value: propertyAddress
    };

    return sectionApi.createSection(payload)
        .then(async section => {
            return sectionApi.updateSection({
                id: section.sectionId,
                path: 'lenderPolicyRecipient',
                value: lender
            });
        });
}